/**
 * 埋点报表导出模板 & 导出记录 API
 * 通过 restApi.js 的 executeSql 方式直接走 DataWorks 路由接口操作 MySQL
 */
import { executeSql } from '@/utils/restApi'

const TEMPLATE_TABLE = 'automatic_report.tracking_export_template_nxqlhhk'
const RECORD_TABLE   = 'automatic_report.tracking_export_record_nxqlhhk'

// ─── 工具 ───────────────────────────────────────────────
function esc(val) {
  if (val === null || val === undefined) return 'NULL'
  return String(val).replace(/\\/g, '\\\\').replace(/'/g, "\\'")
}

function escJson(obj) {
  return esc(JSON.stringify(obj))
}

function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('auth-user') || 'null') || {}
  } catch {
    return {}
  }
}

/** 从 user 对象里取 username（登录名），和字典 export_admin_user_list 里的值对应 */
function getCurrentUsername() {
  const user = getCurrentUser()
  // 按优先级取：username > loginName > login_name，和 restApi.js 保持一致
  return user.username || user.loginName || user.login_name || ''
}

export const EXPORT_RECORD_TIMEOUT_MSG =
  '超时未完成，系统自动标记为失败。建议：请勿在导出完成前关闭导出页面'

/** 无模板导出记录在库里的别名（展示层也可能显示为「通用导出」） */
export const GENERIC_EXPORT_ALIASES = ['无模板临时导出', '通用导出']

export function isGenericExportRecordAlias(alias) {
  const text = (alias || '').trim()
  return !text || GENERIC_EXPORT_ALIASES.includes(text)
}

/** 是否导出记录管理员 */
export function isExportAdmin(adminUsers = []) {
  return adminUsers.includes(getCurrentUsername())
}

/** 是否可编辑/删除模板：管理员可改全部，个人仅可改自己创建的 */
export function canEditTemplate(template, adminUsers = []) {
  if (!template) return false
  if (isExportAdmin(adminUsers)) return true
  return template.created_by === getCurrentUsername()
}

// ─── 模板 CRUD ────────────────────────────────────────────

/**
 * 查询模板列表
 * - 管理员：可见全部（可按 projectCode 过滤）
 * - 个人：公开模板 + 自己创建的
 * 列表不返回 config_json，详情单独查
 */
export async function fetchTemplates(projectCode = null, { adminUsers = [] } = {}) {
  const loginName = esc(getCurrentUsername())
  const isAdmin = isExportAdmin(adminUsers)
  const conditions = ['deleted = 0']
  if (projectCode) conditions.push(`project_code = '${esc(projectCode)}'`)
  if (!isAdmin) conditions.push(`(visibility = 1 OR created_by = '${loginName}')`)
  const sql = `
    SELECT id, alias, project_code, project_name, visibility,
           created_by, created_name, created_at, updated_at
    FROM ${TEMPLATE_TABLE}
    WHERE ${conditions.join(' AND ')}
    ORDER BY updated_at DESC, created_at DESC
  `
  return executeSql(sql)
}

/**
 * 查询模板详情（含 config_json）
 */
export async function fetchTemplateById(id) {
  const sql = `
    SELECT *
    FROM ${TEMPLATE_TABLE}
    WHERE id = ${Number(id)} AND deleted = 0
    LIMIT 1
  `
  const rows = await executeSql(sql)
  return Array.isArray(rows) ? rows[0] : null
}

/**
 * 保存模板（新增）
 */
export async function saveTemplate({ alias, projectCode, projectName, visibility, configJson }) {
  const user = getCurrentUser()
  const loginName = esc(getCurrentUsername())
  const displayName = esc(user.userName || user.name || user.nickName || loginName)
  const sql = `
    INSERT INTO ${TEMPLATE_TABLE}
      (alias, project_code, project_name, visibility, config_json, created_by, created_name)
    VALUES
      ('${esc(alias)}', '${esc(projectCode)}', '${esc(projectName)}', ${visibility ? 1 : 0},
       '${escJson(configJson)}', '${loginName}', '${displayName}')
  `
  return executeSql(sql)
}

/**
 * 更新模板（别名 / 可见性 / 配置）
 */
export async function updateTemplate(id, { alias, visibility, configJson }) {
  const sets = []
  if (alias !== undefined) sets.push(`alias = '${esc(alias)}'`)
  if (visibility !== undefined) sets.push(`visibility = ${visibility ? 1 : 0}`)
  if (configJson !== undefined) sets.push(`config_json = '${escJson(configJson)}'`)
  if (!sets.length) return
  const sql = `UPDATE ${TEMPLATE_TABLE} SET ${sets.join(', ')} WHERE id = ${Number(id)}`
  return executeSql(sql)
}

/**
 * 软删除模板
 */
export async function deleteTemplate(id) {
  const sql = `UPDATE ${TEMPLATE_TABLE} SET deleted = 1 WHERE id = ${Number(id)}`
  return executeSql(sql)
}

// ─── 导出记录 CRUD ────────────────────────────────────────

/**
 * 创建导出记录（初始状态 pending）
 * 用前端生成的 biz_id 唯一标识，插入后通过 biz_id 查回自增 id
 */
export async function createExportRecord({
  templateId = null,
  templateAlias = '',
  projectCode,
  projectName,
  dateStart,
  dateEnd,
  configJson
}) {
  const user = getCurrentUser()
  const loginName = esc(getCurrentUsername())
  const displayName = esc(user.userName || user.name || user.nickName || loginName)
  const tidVal = templateId ? Number(templateId) : 'NULL'
  // 前端生成唯一 biz_id，避免依赖 insertId 返回
  const bizId = `exp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  const insertSql = `
    INSERT INTO ${RECORD_TABLE}
      (biz_id, template_id, template_alias, project_code, project_name,
       date_start, date_end, config_json, status, created_by, created_name)
    VALUES
      ('${bizId}', ${tidVal}, '${esc(templateAlias)}', '${esc(projectCode)}', '${esc(projectName)}',
       '${esc(dateStart)}', '${esc(dateEnd)}', '${escJson(configJson)}',
       'pending', '${loginName}', '${displayName}')
  `
  await executeSql(insertSql)
  // 通过 biz_id 查回自增 id
  const rows = await executeSql(`SELECT id FROM ${RECORD_TABLE} WHERE biz_id = '${bizId}' LIMIT 1`)
  return Array.isArray(rows) && rows[0]?.id ? Number(rows[0].id) : null
}

/**
 * 更新记录状态为 running
 */
export async function markRecordRunning(id) {
  const sql = `
    UPDATE ${RECORD_TABLE}
    SET status = 'running', started_at = NOW()
    WHERE id = ${Number(id)}
  `
  return executeSql(sql)
}

/**
 * 更新记录为成功，写入 result_data
 */
export async function markRecordDone(id, resultData) {
  const size = Array.isArray(resultData) ? resultData.length : 1
  const sql = `
    UPDATE ${RECORD_TABLE}
    SET status = 'done',
        finished_at = NOW(),
        result_data = '${escJson(resultData)}',
        result_size = ${size}
    WHERE id = ${Number(id)}
  `
  return executeSql(sql)
}

/**
 * 更新记录为失败，写入 error_msg
 */
export async function markRecordFailed(id, errorMsg) {
  const sql = `
    UPDATE ${RECORD_TABLE}
    SET status = 'failed',
        finished_at = NOW(),
        error_msg = '${esc(errorMsg)}'
    WHERE id = ${Number(id)}
  `
  return executeSql(sql)
}

/**
 * 查询导出记录列表（不含 result_data，减少传输）
 * 查询前自动把 3 分钟前还在 pending/running 的记录更新为 failed
 */
export async function fetchExportRecords({
  projectCode = null,
  templateId = null,
  templateAlias = null,
  genericExportOnly = false,
  filterProjectCode = null,
  keyword = null,
  status = null,
  adminUsers = [],
  page = 1,
  size = 50
} = {}) {
  const loginName = esc(getCurrentUsername())
  const isAdmin = isExportAdmin(adminUsers)

  // 清理超时记录（非同步，不阻塞）
  const cleanupSql = `
    UPDATE ${RECORD_TABLE}
    SET status = 'failed',
        finished_at = NOW(),
        error_msg = '${esc(EXPORT_RECORD_TIMEOUT_MSG)}'
    WHERE status IN ('pending', 'running')
      AND created_at < DATE_SUB(NOW(), INTERVAL 3 MINUTE)
      AND deleted = 0
  `
  executeSql(cleanupSql).catch(() => {})

  const conditions = ['r.deleted = 0']
  const code = (projectCode || filterProjectCode || '').trim()
  if (code) {
    conditions.push(`r.project_code LIKE '%${esc(code)}%'`)
  }
  if (genericExportOnly) {
    const aliasIn = GENERIC_EXPORT_ALIASES.map((a) => `'${esc(a)}'`).join(', ')
    conditions.push(`(r.template_id IS NULL OR r.template_alias IN (${aliasIn}))`)
  } else if (templateId != null || templateAlias) {
    const tplParts = []
    if (templateId != null && !Number.isNaN(Number(templateId))) {
      tplParts.push(`r.template_id = ${Number(templateId)}`)
    }
    if (templateAlias) {
      tplParts.push(`r.template_alias = '${esc(templateAlias)}'`)
    }
    if (tplParts.length) conditions.push(`(${tplParts.join(' OR ')})`)
  }
  const kw = (keyword || '').trim()
  if (kw) {
    const e = esc(kw)
    conditions.push(`(
      r.project_name LIKE '%${e}%'
      OR r.project_code LIKE '%${e}%'
      OR r.template_alias LIKE '%${e}%'
    )`)
  }
  if (status) conditions.push(`r.status = '${esc(status)}'`)
  if (!isAdmin) conditions.push(`r.created_by = '${esc(loginName)}'`)

  const offset = (page - 1) * size
  const sql = `
    SELECT r.id, r.template_id, r.template_alias, r.project_code, r.project_name,
           r.date_start, r.date_end, r.status, r.error_msg,
           r.result_size, r.created_by, r.created_name,
           r.started_at, r.finished_at, r.created_at
    FROM ${RECORD_TABLE} r
    WHERE ${conditions.join(' AND ')}
    ORDER BY r.created_at DESC
    LIMIT ${size} OFFSET ${offset}
  `
  return executeSql(sql)
}

/**
 * 查询单条记录（含 result_data，用于下载）
 */
export async function fetchRecordWithData(id) {
  const sql = `
    SELECT *
    FROM ${RECORD_TABLE}
    WHERE id = ${Number(id)} AND deleted = 0
    LIMIT 1
  `
  const rows = await executeSql(sql)
  return Array.isArray(rows) ? rows[0] : null
}

/**
 * 软删除记录
 */
export async function deleteExportRecord(id) {
  const sql = `UPDATE ${RECORD_TABLE} SET deleted = 1 WHERE id = ${Number(id)}`
  return executeSql(sql)
}
